import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MenuItem } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import {
  VcuExplorerNode,
  buildExplorerTree,
  getChildrenAtPath,
} from 'src/app/model/vcu-explorer-node';
import { VcuFileItem } from 'src/app/model/response/vcu-files-response';

@Component({
  selector: 'app-vcu-file-explorer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    TableModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ContextMenuModule,
  ],
  templateUrl: './vcu-file-explorer.component.html',
  styleUrls: ['./vcu-file-explorer.component.css'],
})
export class VcuFileExplorerComponent implements OnChanges {
  @Input() maps: VcuFileItem[] = [];
  @Input() logs: VcuFileItem[] = [];
  /** Ids em exclusão — mostra spinner e trava o botão de excluir da respectiva linha. */
  @Input() deletingIds: Set<string> = new Set<string>();
  /** Re-busca em andamento — spinner no botão de atualizar. */
  @Input() refreshing = false;
  /** Somente leitura: esconde ações de gerenciamento (excluir, vincular à VCU, ver
   *  canais) e a barra de seleção em massa. Usado no painel do tuner, onde o tuner
   *  não deve poder alterar arquivos do cliente — só navegar e baixar. */
  @Input() readOnly = false;

  /** Caminho atual no explorer (controlado internamente). */
  currentPath: string = '/';

  /** True em telas mobile (≤700px). No mobile, linha de pasta expande o nome
   *  sobre a coluna de ações (pasta não tem botões). */
  isMobile: boolean = typeof window !== 'undefined' && window.innerWidth <= 700;

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isMobile = window.innerWidth <= 700;
  }

  /** Linha de pasta no mobile com coluna de ações presente → nome faz colspan. */
  folderSpansActions(node: VcuExplorerNode): boolean {
    return node.isFolder && this.isMobile && this.showActionsColumn;
  }

  @Output() openFolder = new EventEmitter<string>();
  @Output() downloadFile = new EventEmitter<{ type: 'map' | 'log'; index: number; file: VcuFileItem }>();
  @Output() downloadSelected = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }[]>();
  @Output() deleteSelected = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }[]>();
  @Output() deleteFile = new EventEmitter<{ type: 'map' | 'log'; index: number; file: VcuFileItem }>();
  @Output() linkVcu = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }>();
  @Output() renameFile = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }>();
  /** Mover um arquivo emite uma lista de um item: um caminho de código, não dois. */
  @Output() moveSelected = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }[]>();
  @Output() viewChannels = new EventEmitter<{ file: VcuFileItem }>();
  @Output() shareFile = new EventEmitter<{ type: 'map' | 'log'; file: VcuFileItem }>();
  /** Pedido de atualização da lista (re-busca no servidor, feito pelo pai). */
  @Output() refreshRequested = new EventEmitter<void>();

  root: VcuExplorerNode | null = null;
  breadcrumbSegments: { label: string; path: string }[] = [];
  currentChildren: VcuExplorerNode[] = [];
  filterText: string = '';

  /** Conjunto de fullPaths dos arquivos selecionados. */
  selectedPaths = new Set<string>();

  /** Nó selecionado para o painel de Detalhes (clique simples). */
  detailNode: VcuExplorerNode | null = null;

  /** Histórico de navegação para voltar/avançar. */
  private navHistory: string[] = ['/'];
  private navIndex = 0;

  /** Menu de linha, compartilhado pelo botão "⋮" e pelo clique direito. */
  @ViewChild('rowMenu') rowMenu!: ContextMenu;
  rowMenuItems: MenuItem[] = [];

  constructor(private translocoService: TranslocoService) {}

  /** Abre o menu da linha. Sobre linha marcada, age na seleção inteira — é o
   *  que o explorador do Windows faz, e evita a surpresa de excluir só uma de
   *  cinco linhas selecionadas. */
  openRowMenu(node: VcuExplorerNode, event: MouseEvent): void {
    if (node.isFolder || !node.file) return;
    this.rowMenuItems = this.isSelected(node) ? this.buildSelectionMenu() : this.buildFileMenu(node);
    this.rowMenu.show(event);
  }

  private buildFileMenu(node: VcuExplorerNode): MenuItem[] {
    const t = (key: string) => this.translocoService.translate(key);
    const isLog = node.typeLabel === 'log';
    const items: MenuItem[] = [
      {
        label: t(isLog ? 'vcuFileExplorer.tooltipViewChannels' : 'vcuFileExplorer.tooltipViewMap'),
        icon: isLog ? 'pi pi-chart-line' : 'pi pi-th-large',
        visible: !this.readOnly,
        command: () => this.emitViewChannels(node),
      },
      { label: t('vcuFileExplorer.download'), icon: 'pi pi-download', command: () => this.emitDownload(node, 0) },
      {
        label: t('vcuFileExplorer.tooltipShare'),
        icon: 'pi pi-share-alt',
        visible: !this.readOnly,
        command: () => this.emitShare(node),
      },
      { separator: true },
      {
        label: t('vcuFileExplorer.tooltipLinkVcu'),
        icon: 'pi pi-link',
        visible: !this.readOnly,
        command: () => this.emitLinkVcu(node),
      },
      {
        label: t('vcuFileExplorer.tooltipRename'),
        icon: 'pi pi-pencil',
        visible: !this.readOnly,
        command: () => this.emitRename(node),
      },
      {
        label: t('vcuFileExplorer.tooltipMove'),
        icon: 'pi pi-folder-open',
        visible: !this.readOnly,
        command: () => this.emitMoveOne(node),
      },
      { label: t('vcuFileExplorer.detailsTitle'), icon: 'pi pi-info-circle', command: () => this.openInfo(node) },
      { separator: true, visible: !this.readOnly },
      {
        label: t('vcuFileExplorer.tooltipDelete'),
        icon: 'pi pi-trash',
        styleClass: 'row-menu-item--danger',
        visible: !this.readOnly,
        disabled: !!node.file && this.deletingIds.has(node.file.id),
        command: () => this.emitDelete(node, 0),
      },
    ];
    return items;
  }

  private buildSelectionMenu(): MenuItem[] {
    const t = (key: string) => this.translocoService.translate(key);
    return [
      { label: t('vcuFileExplorer.download'), icon: 'pi pi-download', command: () => this.emitDownloadSelected() },
      {
        label: t('vcuFileExplorer.tooltipMove'),
        icon: 'pi pi-folder-open',
        visible: !this.readOnly,
        command: () => this.emitMoveSelected(),
      },
      { separator: true, visible: !this.readOnly },
      {
        label: t('vcuFileExplorer.tooltipDelete'),
        icon: 'pi pi-trash',
        styleClass: 'row-menu-item--danger',
        visible: !this.readOnly,
        command: () => this.emitDeleteSelected(),
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maps'] || changes['logs']) {
      this.root = buildExplorerTree(this.maps, this.logs);
      this.selectedPaths.clear();
      this.detailNode = null;
      this.updateBreadcrumbAndChildren();
    }
  }

  // ─── Navegação (toolbar) ────────────────────────────────────────────────
  /** Caminho estilo Vision exibido na barra de endereço. */
  get addressPath(): string {
    return 'C:/FuelTech Vision' + (this.currentPath === '/' ? '' : this.currentPath);
  }

  /** Nome da pasta atual (título acima da tabela). */
  get currentFolderName(): string {
    if (this.currentPath === '/') return 'Arquivos';
    const parts = this.currentPath.split('/').filter(Boolean);
    return parts[parts.length - 1] || 'Arquivos';
  }

  private go(path: string): void {
    this.currentPath = path;
    this.detailNode = null;
    this.updateBreadcrumbAndChildren();
    this.openFolder.emit(path);
  }

  /** Navega empilhando no histórico (uso normal: breadcrumb, abrir pasta, home, subir). */
  navigateTo(path: string): void {
    if (path === this.currentPath) return;
    this.navHistory = this.navHistory.slice(0, this.navIndex + 1);
    this.navHistory.push(path);
    this.navIndex = this.navHistory.length - 1;
    this.go(path);
  }

  goHome(): void { this.navigateTo('/'); }

  goUp(): void {
    if (!this.canGoUp) return;
    const parts = this.currentPath.split('/').filter(Boolean);
    parts.pop();
    this.navigateTo(parts.length ? '/' + parts.join('/') : '/');
  }
  get canGoUp(): boolean { return this.currentPath !== '/'; }

  goBack(): void {
    if (!this.canGoBack) return;
    this.navIndex--;
    this.go(this.navHistory[this.navIndex]);
  }
  get canGoBack(): boolean { return this.navIndex > 0; }

  goForward(): void {
    if (!this.canGoForward) return;
    this.navIndex++;
    this.go(this.navHistory[this.navIndex]);
  }
  get canGoForward(): boolean { return this.navIndex < this.navHistory.length - 1; }

  refresh(): void {
    this.updateBreadcrumbAndChildren();
    this.refreshRequested.emit(); // pede ao pai a re-busca no servidor (traz itens novos)
  }

  // ─── Painel de Detalhes ─────────────────────────────────────────────────
  /** Clique simples: pasta abre (comportamento web); arquivo seleciona p/ detalhes. */
  selectNode(node: VcuExplorerNode): void {
    if (node.isFolder) {
      this.navigateTo(node.fullPath);
      return;
    }
    this.detailNode = node;
  }

  isDetail(node: VcuExplorerNode): boolean {
    return this.detailTarget === node;
  }

  /** Multi-seleção: 2+ arquivos marcados por checkbox. */
  get isMulti(): boolean {
    return this.selectedPaths.size > 1;
  }

  /** Arquivo cujos detalhes o painel mostra: o único marcado (checkbox) ou o
   *  clicado. Com 2+ marcados, o painel vira o resumo de multi-seleção. */
  get detailTarget(): VcuExplorerNode | null {
    if (this.selectedPaths.size === 1) {
      const path = [...this.selectedPaths][0];
      return this.currentChildren.find((c) => c.fullPath === path) ?? this.detailNode;
    }
    return this.detailNode;
  }

  get detailDurationText(): string {
    const n = this.detailTarget;
    return n ? this.getLogDuration(n) : '---';
  }

  /** Modal de info (botão "info" da linha) — mesmo conteúdo da lateral. */
  showInfo = false;
  infoNode: VcuExplorerNode | null = null;

  openInfo(node: VcuExplorerNode): void {
    if (node.isFolder || !node.file) return;
    this.infoNode = node;
    this.showInfo = true;
  }

  closeInfo(): void {
    this.showInfo = false;
    this.infoNode = null;
  }

  /** Se a pasta atual tem pelo menos um arquivo (define se há coluna de checkbox). */
  get hasFileInCurrentView(): boolean {
    return this.currentChildren.some((c) => !c.isFolder);
  }

  /** Coluna de ações: só quando há arquivo na pasta e nada marcado. Com seleção
   *  ativa a barra assume as ações e a coluna sai — senão ficariam dois conjuntos
   *  de botões na tela sem deixar claro sobre qual arquivo cada um age. */
  get showActionsColumn(): boolean {
    return this.hasFileInCurrentView && !this.hasSelection;
  }

  /** Colunas fixas (nome, tipo, duração, modificado, criado) + as condicionais. */
  get columnCount(): number {
    return 5 + (this.hasFileInCurrentView ? 1 : 0) + (this.showActionsColumn ? 1 : 0);
  }

  /** Único arquivo marcado — com um só, a barra mostra o conjunto completo de ações. */
  get singleSelectedNode(): VcuExplorerNode | null {
    if (this.selectedPaths.size !== 1) return null;
    const path = [...this.selectedPaths][0];
    return this.currentChildren.find((c) => c.fullPath === path) ?? null;
  }

  clearSelection(): void {
    this.selectedPaths.clear();
  }

  /** Filho atual filtrado pelo texto do filtro. */
  get filteredChildren(): VcuExplorerNode[] {
    if (!this.filterText || !this.filterText.trim()) return this.currentChildren;
    const q = this.filterText.trim().toLowerCase();
    return this.currentChildren.filter((c) => c.name.toLowerCase().includes(q));
  }

  get selectableFiles(): VcuExplorerNode[] {
    return this.filteredChildren.filter((c) => !c.isFolder && c.file);
  }

  get allFilesSelected(): boolean {
    const files = this.selectableFiles;
    return files.length > 0 && files.every((f) => this.selectedPaths.has(f.fullPath));
  }

  get hasSelection(): boolean {
    return this.selectedPaths.size > 0;
  }

  isSelected(node: VcuExplorerNode): boolean {
    return this.selectedPaths.has(node.fullPath);
  }

  toggleRow(node: VcuExplorerNode): void {
    if (node.isFolder || !node.file) return;
    if (this.selectedPaths.has(node.fullPath)) {
      this.selectedPaths.delete(node.fullPath);
    } else {
      this.selectedPaths.add(node.fullPath);
    }
  }

  toggleSelectAll(): void {
    const files = this.selectableFiles;
    if (this.allFilesSelected) {
      files.forEach((f) => this.selectedPaths.delete(f.fullPath));
    } else {
      files.forEach((f) => this.selectedPaths.add(f.fullPath));
    }
  }

  /** Arquivos marcados agora, no formato que os emitters em lote publicam. */
  private selectedItems(): { type: 'map' | 'log'; file: VcuFileItem }[] {
    const items: { type: 'map' | 'log'; file: VcuFileItem }[] = [];
    for (const node of this.selectableFiles) {
      if (this.selectedPaths.has(node.fullPath) && node.file) {
        items.push({ type: node.typeLabel === 'map' ? 'map' : 'log', file: node.file });
      }
    }
    return items;
  }

  emitDownloadSelected(): void {
    const items = this.selectedItems();
    if (items.length === 0) return;
    this.downloadSelected.emit(items);
  }

  emitDeleteSelected(): void {
    const items = this.selectedItems();
    if (items.length === 0) return;
    this.deleteSelected.emit(items);
  }

  emitMoveSelected(): void {
    const items = this.selectedItems();
    if (items.length === 0) return;
    this.moveSelected.emit(items);
  }

  private updateBreadcrumbAndChildren(): void {
    if (!this.root) return;
    this.currentChildren = getChildrenAtPath(this.root, this.currentPath);
    this.buildBreadcrumb();
  }

  private buildBreadcrumb(): void {
    const path = this.currentPath || '/';
    const parts = path.split('/').filter(Boolean);
    this.breadcrumbSegments = [{ label: 'Arquivos', path: '/' }];
    let acc = '';
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : `/${p}`;
      this.breadcrumbSegments.push({ label: p, path: acc });
    }
  }

  onBreadcrumbClick(path: string): void {
    this.navigateTo(path);
  }

  /** Duplo clique em arquivo abre a visualização (pasta já abre no clique simples).
   *  Em modo readOnly não há ação de visualizar canais wireada (ver `readOnly`). */
  onRowClick(node: VcuExplorerNode): void {
    if (!node.isFolder && !this.readOnly) {
      this.emitViewChannels(node);
    }
  }

  getTypeLabel(node: VcuExplorerNode): string {
    if (node.typeLabel === 'folder') return 'Pasta';
    if (node.typeLabel === 'map') return 'Mapa';
    return 'Log';
  }

  getLogDuration(node: VcuExplorerNode): string {
    if (node.typeLabel !== 'log' || node.logDuration == null) return '---';
    return String(node.logDuration);
  }

  getModifiedDate(node: VcuExplorerNode): string {
    return node.modifiedDate || node.createdDate || '---';
  }

  getCreatedDate(node: VcuExplorerNode): string {
    return node.createdDate || '---';
  }

  trackByIndex(index: number): number {
    return index;
  }

  emitDownload(node: VcuExplorerNode, index: number): void {
    if (!node.file) return;
    const type = node.typeLabel === 'map' ? 'map' : 'log';
    const list = type === 'map' ? this.maps : this.logs;
    const fileIndex = list.findIndex((f) => f.id === node.file!.id);
    this.downloadFile.emit({ type, index: fileIndex >= 0 ? fileIndex : index, file: node.file });
  }

  emitDelete(node: VcuExplorerNode, index: number): void {
    if (!node.file) return;
    const type = node.typeLabel === 'map' ? 'map' : 'log';
    const list = type === 'map' ? this.maps : this.logs;
    const fileIndex = list.findIndex((f) => f.id === node.file!.id);
    this.deleteFile.emit({ type, index: fileIndex >= 0 ? fileIndex : index, file: node.file });
  }

  emitLinkVcu(node: VcuExplorerNode): void {
    if (!node.file) return;
    const type: 'map' | 'log' = node.typeLabel === 'map' ? 'map' : 'log';
    this.linkVcu.emit({ type, file: node.file });
  }

  emitRename(node: VcuExplorerNode): void {
    if (!node.file) return;
    const type: 'map' | 'log' = node.typeLabel === 'map' ? 'map' : 'log';
    this.renameFile.emit({ type, file: node.file });
  }

  emitShare(node: VcuExplorerNode): void {
    if (!node.file) return;
    const type: 'map' | 'log' = node.typeLabel === 'map' ? 'map' : 'log';
    this.shareFile.emit({ type, file: node.file });
  }

  /** Mover um arquivo só reusa o caminho do lote, com uma lista de um item. */
  emitMoveOne(node: VcuExplorerNode): void {
    if (!node.file) return;
    const type: 'map' | 'log' = node.typeLabel === 'map' ? 'map' : 'log';
    this.moveSelected.emit([{ type, file: node.file }]);
  }

  /** Logs abrem canais+mapa; mapas standalone (.ftm/.ftmap) abrem em modo só-mapa. */
  emitViewChannels(node: VcuExplorerNode): void {
    if (!node.file) return;
    this.viewChannels.emit({ file: node.file });
  }
}
